




































































































































import LazyHydrate from "vue-lazy-hydration";
import { SfSelect, SfHeading } from "@storefront-ui/vue";
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useFetch,
  useAsync,
  useContext,
  watch,
} from "@nuxtjs/composition-api";
import { CacheTagPrefix, useCache } from "@vue-storefront/cache";
import { usePageStore } from "~/stores/page";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import CategoryPagination from "~/modules/catalog/category/components/pagination/CategoryPagination.vue";
import { useCategory, useFacet, useUiHelpers, useUiState } from "~/composables";

import { useAddToCart } from "~/helpers/cart/addToCart";
import { useWishlist } from "~/modules/wishlist/composables/useWishlist";
import { usePrice } from "~/modules/catalog/pricing/usePrice";
import { useCategoryContent } from "~/modules/catalog/category/components/cms/useCategoryContent";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";
import facetGetters from "~/modules/catalog/category/getters/facetGetters";
import { getMetaInfo } from "~/helpers/getMetaInfo";

import CategoryNavbar from "~/modules/catalog/category/components/navbar/CategoryNavbar.vue";
import CategoryBreadcrumbs from "~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue";

import type { ProductInterface } from "~/modules/GraphQL/types";
import type { SortingModel } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import type { Pagination } from "~/composables/types";
import type { Product } from "~/modules/catalog/product/types";

// import useStock from "~/composables/useStock";
import useSalePrice from "~/composables/useSalePrice";

import { useScrollPageStore } from "~/stores/scrollpage";

export default defineComponent({
  name: "CategoryPage",
  components: {
    CategoryPagination,
    CategoryEmptyResults: () =>
      import("~/modules/catalog/category/components/CategoryEmptyResults.vue"),
    CategoryFilters: () =>
      import("~/modules/catalog/category/components/filters/CategoryFilters.vue"),
    CategoryProductGrid: () =>
      import("~/modules/catalog/category/components/views/CategoryProductGrid.vue"),
    CategoryShowChildren: () =>
      import("~/modules/catalog/category/components/CategoryShowChildren.vue"),
    CategoryNavbar,
    CategoryBreadcrumbs,
    SfSelect,
    LazyHydrate,
    SfHeading,
    SkeletonLoader,
  },
  transition: "fade",
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: "", options: [] });
    const pagination = ref<Pagination>({});
    const currentPage = ref(1);
    const shouldShowLoadMore = ref(true);

    const scrollPageStore = useScrollPageStore();
    const pageScroll: any = computed(() => scrollPageStore.scrollPosition);

    const { i18n, $config } = useContext();
    const locale = i18n.locale;
    const base_url = $config.baseUrl;

    const showSkeleton = ref(true);

    // const { getMultipleProductStocks } = useStock();
    const { getSalePrices } = useSalePrice();

    const stockData = ref(null);
    const priceData = ref(null);

    const querySalePrices = async () => {
      const skus = products.value.map((p) => {
        return {
          itemNo: p.sku,
          quantity: 1,
        };
      });
      priceData.value = await getSalePrices(skus);
    };

    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      toggleFilterSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isCategoryGridView,
      isFilterSidebarOpen,
    } = useUiState();
    const {
      load: loadWishlist,
      addItem: addItemToWishlistBase,
      isInWishlist,
      removeItem: removeItemFromWishlist,
    } = useWishlist();
    const { result, search, loading } = useFacet();
    const { addItemToCart } = useAddToCart();

    const categoryMeta = ref(null);

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product })
        ? removeItemFromWishlist({ product })
        : addItemToWishlistBase({ product }));
    };

    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryName = computed(() => activeCategory.value?.name ?? "");
    const activeCategoryImage = computed(() => activeCategory.value?.image ?? "");
    const activeCategoryDescription = computed(
      () => activeCategory.value?.description ?? ""
    );
    const activeCategoryShowChildren = computed(
      () => activeCategory.value?.show_children_categories ?? false
    );

    const categoryUid = routeData.uid;

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
        await loadCategoryTree();
      }
      const [content, categoryMetaData] = await Promise.all([
        getContentData(categoryUid as string),
        loadCategoryMeta({ category_uid: categoryUid }),
      ]);

      if (!activeCategoryShowChildren.value) {
        await search({
          ...uiHelpers.getFacetsFromURL(),
          category_uid: categoryUid,
        });
      } else if (activeCategory.value.show_products.length > 0) {
        const products_skus = activeCategory.value.show_products.split(",");
        await search({
          ...{ itemsPerPage: 20 },
          sku: { in: products_skus },
          category_uid: categoryUid,
        });
      }

      categoryMeta.value = categoryMetaData;

      products.value = facetGetters.getProducts(result.value) ?? [];

      sortBy.value = facetGetters.getSortOptions(result.value);
      pagination.value = facetGetters.getPagination(result.value);

      if (
        products.value.length === pagination.value.totalItems ||
        pagination.value.totalItems < 20
      ) {
        shouldShowLoadMore.value = false;
      }

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));

      addTags([...tags, ...productTags]);
      isShowProducts.value = content.isShowProducts;

      // const skus = products.value.map((item) => item.sku);
      // stockData.value = await getMultipleProductStocks(skus);
      querySalePrices();
    });

    useAsync(async () => {
      if (process.client && !stockData.value) {
        fetch();
      }
    });

    const isPriceLoaded = ref(false);
    const showMobileFilters = ref(false);
    const toggleFilters = () => {
      showMobileFilters.value = !showMobileFilters.value;
    };
    onMounted(async () => {
      loadWishlist();
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)
            ?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
      }

      isPriceLoaded.value = true;
      showSkeleton.value = false;
    });

    const goToPage = async (page) => {
      currentPage.value = page;
      uiHelpers.changePage(page, true);
      scrollPageStore.setChangePagePosition({ x: 0, y: window.scrollY });
      fetch();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      localStorage.setItem("items-per-page", JSON.stringify(itemsPerPage));
      uiHelpers.changeItemsPerPage(itemsPerPage, true);
      goToPage(0);
    };

    const onReloadProducts = () => {
      goToPage(0);
    };

    watch(
      products,
      (val) => {
        if (val.length > 0) {
          if (process.client) {
            setTimeout(() => {
              window.scrollTo(pageScroll.value.x, pageScroll.value.y);
            }, 500);
          }
        }
      },
      { immediate: false, deep: true }
    );

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      isCategoryGridView,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      sortBy,
      isShowProducts,
      activeCategoryName,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      activeCategoryImage,
      activeCategoryDescription,
      currentPage,
      shouldShowLoadMore,
      activeCategoryShowChildren,
      activeCategory,
      stockData,
      showMobileFilters,
      toggleFilters,
      priceData,
      locale,
      base_url,
      showSkeleton,
    };
  },
  head() {
    return getMetaInfo(this.categoryMeta, this.locale, this.base_url);
  },
});
